import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Modal, Button, Field, Input, Spinner } from "./ui";
import { getSpaceIcon, SPACES_LIBRARY, SPACES_BY_KEY } from "../lib/templates";
import EntityFocus from "./EntityFocus";
import { Plus, Trash2 } from "lucide-react";
import { cn } from "../lib/utils";

function lstr(obj, lang) { return obj ? (obj[lang] ?? obj.fr ?? "") : ""; }

export default function SpacesView({ entity }) {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [picked, setPicked] = useState("general");
  const [customName, setCustomName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/spaces?entity_id=${entity.id}`);
      setSpaces(data);
    } finally { setLoading(false); }
  }, [entity.id]);

  useEffect(() => { load(); }, [load]);

  const existingNames = new Set(spaces.map((s) => s.name.toLowerCase()));

  const addSpace = async () => {
    const lib = SPACES_BY_KEY[picked];
    const name = picked === "custom" ? customName.trim() : lstr(lib?.label, lang);
    if (!name) return;
    setSaving(true);
    try {
      await api.post("/spaces", {
        entity_id: entity.id, name,
        description: picked === "custom" ? "" : lstr(lib?.desc, lang),
        icon: picked === "custom" ? "layout-grid" : lib?.icon,
        color: entity.color,
      });
      setShowAdd(false); setCustomName(""); setPicked("general");
      await load();
    } finally { setSaving(false); }
  };

  const removeSpace = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm(lang === "fr" ? "Supprimer cet espace et tout son contenu ?" : "Delete this space and all its content?")) return;
    await api.delete(`/spaces/${id}`);
    await load();
  };

  if (loading) return <div className="py-16 flex justify-center"><Spinner className="w-7 h-7 text-text-secondary" /></div>;

  return (
    <div data-testid="spaces-view">
      <EntityFocus entity={entity} />

      <div className="flex items-center justify-between mb-5">
        <p className="text-text-secondary text-sm">{t("spaces_intro")}</p>
        <Button onClick={() => setShowAdd(true)} data-testid="add-space-button"><Plus className="w-4 h-4" /> {t("add_space")}</Button>
      </div>

      {spaces.length === 0 ? (
        <Card className="text-center py-12 text-text-secondary text-sm">{t("no_spaces")}</Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {spaces.map((s) => {
            const Icon = getSpaceIcon(s.icon);
            return (
              <Card key={s.id} hover onClick={() => navigate(`/entity/${entity.key}/space/${s.id}`)}
                data-testid={`space-card-${s.id}`} className="group relative">
                <button onClick={(e) => removeSpace(e, s.id)} data-testid={`space-delete-${s.id}`}
                  className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 text-text-secondary hover:text-red-400 transition-all">
                  <Trash2 className="w-4 h-4" />
                </button>
                <span className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${entity.color}22` }}>
                  <Icon className="w-5 h-5" style={{ color: entity.color }} />
                </span>
                <h4 className="font-heading font-medium">{s.name}</h4>
                {s.description && <p className="text-text-secondary text-sm mt-1 leading-snug">{s.description}</p>}
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title={t("add_space")}
        footer={<>
          <Button variant="outline" onClick={() => setShowAdd(false)}>{t("cancel")}</Button>
          <Button onClick={addSpace} disabled={saving || (picked === "custom" && !customName.trim())} data-testid="space-save">
            {saving && <Spinner className="w-3.5 h-3.5" />}{t("create")}
          </Button>
        </>}>
        <Field label={t("choose_space")}>
          <div className="grid grid-cols-2 gap-2" data-testid="space-picker">
            {SPACES_LIBRARY.filter((s) => !existingNames.has(lstr(s.label, lang).toLowerCase())).map((s) => {
              const Icon = getSpaceIcon(s.icon);
              const on = picked === s.key;
              return (
                <button key={s.key} type="button" onClick={() => setPicked(s.key)} data-testid={`space-opt-${s.key}`}
                  className={cn("flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all",
                    on ? "border-transparent ring-2 ring-[var(--text-primary)]" : "border-[var(--border)] hover:bg-surface-hover")}>
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${entity.color}22` }}>
                    <Icon className="w-4 h-4" style={{ color: entity.color }} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium leading-tight">{lstr(s.label, lang)}</span>
                    <span className="block text-[11px] text-text-secondary mt-0.5 leading-snug">{lstr(s.desc, lang)}</span>
                  </span>
                </button>
              );
            })}
            <button type="button" onClick={() => setPicked("custom")} data-testid="space-opt-custom"
              className={cn("flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all",
                picked === "custom" ? "border-transparent ring-2 ring-[var(--text-primary)]" : "border-[var(--border)] hover:bg-surface-hover")}>
              <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${entity.color}22` }}>
                <Plus className="w-4 h-4" style={{ color: entity.color }} />
              </span>
              <span className="text-sm font-medium">{t("custom_space")}</span>
            </button>
          </div>
        </Field>
        {picked === "custom" && (
          <Field label={t("space_name")}>
            <Input value={customName} onChange={(e) => setCustomName(e.target.value)} data-testid="space-custom-name" autoFocus />
          </Field>
        )}
      </Modal>
    </div>
  );
}
